import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ReactiveContainer } from '../../containers/reactive-container';
import { ApolloService } from '../../services/apollo.service';
import { RolesService } from '../../services/roles/roles.service';
import { ToastService } from '../../services/toast/toast.service';
import * as fromGraphQl from './graphql';

interface NotificationCategoryGroup {
  categoryKey: string;
  categoryLabel: string;
  types: fromGraphQl.NotificationTypeInfo[];
}

/**
 * Notification-Einstellungen im Nutzerprofil (#735).
 *
 * Selbst-scoped: bekommt keinen Input, insbesondere keine userId — die Operationen beziehen
 * sich serverseitig auf den aufrufenden Nutzer.
 *
 * Datenzugriff bewusst ueber ReactiveContainer und NICHT ueber die Container-Basisklasse des
 * Profil-Screens: nur ReactiveContainer liefert loading/error als Signale, Container verwirft
 * GraphQL-Fehler in einer leeren Schleife.
 */
@Component({
  selector: 'app-notification-settings-list',
  templateUrl: './notification-settings-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationSettingsListComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly toastService = inject(ToastService);
  private readonly rolesService = inject(RolesService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly container = new ReactiveContainer<fromGraphQl.CatalogQueryResponse>(
    fromGraphQl.NOTIFICATION_CATALOG_QUERY,
    { useGlobalLoading: false }
  );

  // Muessen public sein: strictTemplates erlaubt keinen Template-Zugriff auf private Felder.
  readonly loading = this.container.loading;
  readonly error = this.container.error;

  /** Alleinige Anzeigequelle nach der Erstbefuellung — siehe effect() im Konstruktor. */
  readonly types = signal<fromGraphQl.NotificationTypeInfo[]>([]);
  readonly lockedTypes = signal<ReadonlySet<string>>(new Set<string>());
  readonly sendingTests = signal<ReadonlySet<string>>(new Set<string>());
  readonly writeError = signal<string | null>(null);

  readonly canSendTest = this.rolesService.hasRole('framework_nexato_employee');

  /**
   * Gruppierung nach categoryLabel. Die Reihenfolge kommt vom Server
   * (categoryKey, sortOrder, typeKey) und wird UNVERAENDERT uebernommen — hier wird
   * ausdruecklich nicht nachsortiert.
   *
   * Ausgeblendet wird nur ein Typ ohne JEDEN gelieferten Kanal (Entscheidung 2a). Ein Typ mit
   * ausschliesslich erzwungenen Kanaelen bleibt sichtbar — der Nutzer bekommt ihn zugestellt,
   * er soll ihn also auch sehen. Der Fall "gar kein Kanal" ist durch die Server-Invariante
   * (IN_APP ist nie HIDDEN, NotificationTypeRegistry) praktisch unerreichbar und hier nur
   * defensiv abgedeckt.
   */
  readonly groups = computed<NotificationCategoryGroup[]>(() => {
    const groups: NotificationCategoryGroup[] = [];
    for (const type of this.types()) {
      if (!type.channels?.length) {
        continue;
      }
      const last = groups[groups.length - 1];
      if (last && last.categoryKey === type.categoryKey) {
        last.types.push(type);
      } else {
        groups.push({
          categoryKey: type.categoryKey,
          categoryLabel: type.categoryLabel,
          types: [type]
        });
      }
    }
    return groups;
  });

  readonly isEmpty = computed(() => this.groups().length === 0);

  constructor() {
    // Erstbefuellung EINMALIG. Der Guard ist Pflicht: _data wird bei jeder Emission gesetzt,
    // ein ungeschuetztes set() wuerde optimistisch umgeschaltete und gepatchte Werte
    // ueberschreiben. allowSignalWrites ist in Angular 18 zwingend (sonst NG0600).
    effect(() => {
      const catalog = this.container.data()?.catalog;
      if (!catalog?.length) {
        return;
      }
      if (this.types().length) {
        return;
      }
      this.types.set(catalog);
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.container.execute({ locale: this.currentLocale() });
  }

  isLocked(typeKey: string): boolean {
    return this.lockedTypes().has(typeKey);
  }

  isSendingTest(typeKey: string): boolean {
    return this.sendingTests().has(typeKey);
  }

  /**
   * Optimistisch umschalten -> Mutation -> Antwort in den Zustand patchen; bei Fehler
   * zurueckdrehen. Kein Refetch des Katalogs, die Mutation liefert den aktualisierten Typ.
   */
  onToggle(type: fromGraphQl.NotificationTypeInfo, event: { channel: fromGraphQl.NotificationChannelKey; enabled: boolean }): void {
    if (this.isLocked(type.typeKey)) {
      return;
    }
    // Nur den Ist-Wert DIESES Kanals merken, nicht den ganzen Zustand: Zeilen sind einzeln
    // gesperrt, es koennen also zwei gleichzeitig fliegen. Ein Voll-Array-Rueckdreher wuerde
    // den serverbestaetigten Patch einer anderen Zeile verwerfen.
    const previousEnabled = type.channels
      .find((c) => c.channel === event.channel)?.effectiveEnabled ?? !event.enabled;

    this.setLocked(type.typeKey, true);
    this.applyOptimisticToggle(type.typeKey, event.channel, event.enabled);

    this.apolloService
      .mutateObservable<fromGraphQl.UpdatePreferenceResponse>(
        fromGraphQl.UPDATE_NOTIFICATION_PREFERENCE_MUTATION,
        { input: { typeKey: type.typeKey, channel: event.channel, enabled: event.enabled } }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const updated = result?.data?.updateNotificationPreference;
          if (updated) {
            // Der Server ist die Wahrheit ueber die Policy.
            this.types.update((types) => types.map((t) => (t.typeKey === updated.typeKey ? updated : t)));
          }
          this.writeError.set(null);
          this.setLocked(type.typeKey, false);
        },
        error: () => {
          this.applyOptimisticToggle(type.typeKey, event.channel, previousEnabled);
          this.writeError.set('core.user.profile.notifications.updateErrorMessage');
          this.toastService.addErrorToast('core.user.profile.notifications.updateErrorToastMessage');
          this.setLocked(type.typeKey, false);
        }
      });
  }

  /**
   * Test-Send laeuft ueber den normalen Publish-Weg und hat kein Rate-Limiting —
   * daher die In-Flight-Sperre. Ein blankes false gilt als Fehler, nicht als Erfolg.
   */
  onSendTest(type: fromGraphQl.NotificationTypeInfo): void {
    if (this.isSendingTest(type.typeKey)) {
      return;
    }
    this.setSendingTest(type.typeKey, true);

    this.apolloService
      .mutateObservable<fromGraphQl.SendTestNotificationResponse>(
        fromGraphQl.SEND_TEST_NOTIFICATION_MUTATION,
        { typeKey: type.typeKey }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result?.data?.sendTestNotification === true) {
            this.toastService.addToastWithMessage('core.user.profile.notifications.sendTestSuccessToastMessage');
          } else {
            this.toastService.addErrorToast('core.user.profile.notifications.sendTestErrorToastMessage');
          }
          this.setSendingTest(type.typeKey, false);
        },
        error: () => {
          this.toastService.addErrorToast('core.user.profile.notifications.sendTestErrorToastMessage');
          this.setSendingTest(type.typeKey, false);
        }
      });
  }

  /**
   * currentLang ist in @ngx-translate/core 16 ein String-Getter, kein Signal.
   * Der Katalog wird bewusst nicht bei Sprachwechsel neu geladen (#735).
   */
  private currentLocale(): string {
    return this.translate.currentLang ?? this.translate.getDefaultLang() ?? 'de';
  }

  private applyOptimisticToggle(typeKey: string, channel: fromGraphQl.NotificationChannelKey, enabled: boolean): void {
    this.types.update((types) =>
      types.map((type) => {
        if (type.typeKey !== typeKey) {
          return type;
        }
        return {
          ...type,
          channels: type.channels.map((c) => (c.channel === channel ? { ...c, effectiveEnabled: enabled } : c))
        };
      })
    );
  }

  private setLocked(typeKey: string, locked: boolean): void {
    this.lockedTypes.update((current) => {
      const next = new Set(current);
      if (locked) {
        next.add(typeKey);
      } else {
        next.delete(typeKey);
      }
      return next;
    });
  }

  private setSendingTest(typeKey: string, sending: boolean): void {
    this.sendingTests.update((current) => {
      const next = new Set(current);
      if (sending) {
        next.add(typeKey);
      } else {
        next.delete(typeKey);
      }
      return next;
    });
  }
}
